const accessTypeHierarchy = ['regular', 'administrator', 'primary'];

const accountPermissions = ['rolesAndCoursesEditor'];

const projectPermissions = [
  'projectViewer',
  'projectEditor',
  'projectApprover',
  'contentEditor',
];

const permissionsExcludingAdmin = [
  ...projectPermissions,
  'personnelViewer',
  'personnelEditor',
  'personnelReporteeManager',
];

const permissions = ['administrator', ...permissionsExcludingAdmin];

const fullUserAllowanceConsumingPermissions = permissions.filter(
  (permission) =>
    !['personnelReporteeManager', 'projectViewer'].includes(permission),
);

const personnelReporteeManagerAllowanceConsumingPermissions = [
  'personnelReporteeManager',
];

const inferredPermissions = {
  administrator: permissionsExcludingAdmin,
  contentEditor: ['projectViewer', 'projectEditor'],
  projectEditor: ['projectViewer'],
  projectApprover: ['projectViewer'],
  personnelEditor: ['personnelViewer'],
};

const defaultPermissionStateValues = Object.fromEntries(
  [...permissions, ...accountPermissions].map((permission) => [
    permission,
    'unselected',
  ]),
);

function personDisplayName({ attributes }) {
  return [attributes.firstName, attributes.lastName].filter(Boolean).join(' ');
}

function isPermissionSelected(value) {
  return ['activelySelected', 'inferredSelected'].includes(value);
}

function isPermissionActivelySelected(value) {
  return value === 'activelySelected';
}

function isAnyPermissionSelected({ accessSelection, permissionsToCheck }) {
  return permissionsToCheck.some((permission) =>
    isPermissionSelected(accessSelection?.[permission]),
  );
}

function isAnyPermissionActivelySelected({
  accessSelection,
  permissionsToCheck,
}) {
  return permissionsToCheck.some((permission) =>
    isPermissionActivelySelected(accessSelection?.[permission]),
  );
}

function isAnyAssignablePermissionSelected({
  accessSelection,
  assignablePermissions,
  permissionsToCheck,
}) {
  return (
    selectedAssignablePermissions({
      accessSelection,
      assignablePermissions,
      permissionsToCheck,
    }).length > 0
  );
}

function selectedAssignablePermissions({
  accessSelection,
  assignablePermissions = [],
  permissionsToCheck,
}) {
  return permissionsToCheck.filter(
    (permission) =>
      assignablePermissions.includes(permission) &&
      isPermissionSelected(accessSelection?.[permission]),
  );
}

function isAccountPermissionInferable({
  accessSelection,
  assignablePermissions = [],
  permission,
}) {
  return (
    permissions.some(
      (divisionPermission) =>
        assignablePermissions.includes(divisionPermission) &&
        isPermissionSelected(accessSelection?.[divisionPermission]),
    ) && assignablePermissions.includes(permission)
  );
}

function doesUserConsumeFullUserAllowance({
  accessControls,
  accessType,
  rolesAndCoursesEditor,
}) {
  if (accessType !== 'regular') return true;
  if (accessControls.length === 0) return false;
  if (rolesAndCoursesEditor) return true;

  return accessControls.some(({ attributes }) =>
    fullUserAllowanceConsumingPermissions.some(
      (permission) => attributes[permission],
    ),
  );
}

function doesUserConsumePersonnelReporteeManagerAllowance({
  accessControls,
  accessType,
  assignableDivisionIds,
  rolesAndCoursesEditor,
}) {
  if (accessType !== 'regular') return false;
  if (
    doesUserConsumeFullUserAllowance({
      accessControls,
      accessType,
      rolesAndCoursesEditor,
    })
  )
    return false;

  return accessControls.some(
    ({ attributes, relationships }) =>
      assignableDivisionIds.includes(relationships.company.data.id) &&
      personnelReporteeManagerAllowanceConsumingPermissions.some(
        (permission) => attributes[permission],
      ),
  );
}

// convert string values back to booleans. If the value is 'activelySelected' or 'inferredSelected' it sends true to the backend
function buildAccessSelectionAttributes({ accessSelection }) {
  const permissionSelectionsAsDomainPermissions = Object.fromEntries(
    permissions.map((permission) => [
      permission,
      isPermissionSelected(accessSelection[permission]),
    ]),
  );

  return {
    administrator: permissionSelectionsAsDomainPermissions.administrator,
    project_approver: permissionSelectionsAsDomainPermissions.projectApprover,
    project_viewer: permissionSelectionsAsDomainPermissions.projectViewer,
    project_editor: permissionSelectionsAsDomainPermissions.projectEditor,
    content_editor: permissionSelectionsAsDomainPermissions.contentEditor,
    personnel_editor: permissionSelectionsAsDomainPermissions.personnelEditor,
    personnel_viewer: permissionSelectionsAsDomainPermissions.personnelViewer,
    personnel_reportee_manager:
      permissionSelectionsAsDomainPermissions.personnelReporteeManager,
  };
}

function buildAccessControlAttributes({
  accessSelection,
  divisionId,
  domainUser,
}) {
  const accessControlId = domainUser?.included?.find(
    (inclusion) =>
      inclusion.type === 'accessControl' &&
      inclusion.relationships.company.data.id === divisionId,
  )?.id;

  const accessControlAttributes =
    accessControlId ? { id: accessControlId } : {};

  if (accessSelection) {
    Object.assign(
      accessControlAttributes,
      buildAccessSelectionAttributes({ accessSelection }),
      { company_id: divisionId },
    );
  } else {
    Object.assign(accessControlAttributes, { _destroy: true });
  }

  return accessControlAttributes;
}

function userParamsFromState({ currentUser, domainUser }) {
  const userParams = {
    first_name: currentUser.firstName,
    last_name: currentUser.lastName,
    email: currentUser.email,
    position: currentUser.position,
    access_type: currentUser.accessType,
  };

  const isAccessTypeRegular = currentUser.accessType === 'regular';
  const accessSelections = Object.values(currentUser.accessControls);

  if (isAccessTypeRegular) {
    userParams.access_controls_attributes = Object.entries(
      currentUser.accessControls,
    ).reduce((attributes, [divisionId, accessSelection]) => {
      attributes[divisionId] = buildAccessControlAttributes({
        accessSelection,
        divisionId,
        domainUser,
      });
      return attributes;
    }, {});
  }

  if (isAccessTypeRegular || accessSelections.length > 0) {
    userParams.roles_and_courses_editor = accessSelections.some(
      (accessSelection) =>
        isPermissionSelected(accessSelection?.rolesAndCoursesEditor),
    );
  }

  return userParams;
}

function applyInferredSelections({ accessSelection, isSelected, permission }) {
  const shouldBeInferredSelected = (inferredPermission) => {
    switch (inferredPermission) {
      case 'projectViewer':
        return isAnyPermissionActivelySelected({
          accessSelection,
          permissionsToCheck: [
            'projectApprover',
            'projectEditor',
            'contentEditor',
          ],
        });
      case 'projectEditor':
        return isPermissionActivelySelected(accessSelection.contentEditor);
      case 'personnelViewer':
        return isPermissionActivelySelected(accessSelection.personnelEditor);
      default:
        return false;
    }
  };
  inferredPermissions[permission].forEach((inferredPermission) => {
    if (isPermissionActivelySelected(accessSelection[inferredPermission]))
      return;

    accessSelection[inferredPermission] =
      isSelected || shouldBeInferredSelected(inferredPermission) ?
        'inferredSelected'
      : 'unselected';
  });
}

function computeAccessSelection({
  currentAccessSelection,
  isSelected,
  permission,
}) {
  const accessSelection = {
    ...currentAccessSelection,
    [permission]: isSelected ? 'activelySelected' : 'unselected',
  };

  if (Object.keys(inferredPermissions).includes(permission)) {
    applyInferredSelections({
      accessSelection,
      isSelected,
      permission,
    });
  }

  return accessSelection;
}

export {
  accessTypeHierarchy,
  accountPermissions,
  computeAccessSelection,
  defaultPermissionStateValues,
  doesUserConsumeFullUserAllowance,
  doesUserConsumePersonnelReporteeManagerAllowance,
  isAccountPermissionInferable,
  isAnyAssignablePermissionSelected,
  isAnyPermissionActivelySelected,
  isAnyPermissionSelected,
  isPermissionActivelySelected,
  isPermissionSelected,
  permissions,
  permissionsExcludingAdmin,
  personDisplayName,
  projectPermissions,
  userParamsFromState,
};
